<template>
  <div
    id="ride-ordering-v2"
    class="ride-ordering-v2 page-content"
  >
    <div class="ordering-container">
      <div class="panel-header">
        <h3 class="panel-title">
          Ride Ordering
        </h3>
        <p class="panel-subtitle">
          {{ panelSubtitle }}
        </p>
      </div>
      <div class="panel-content">
        <div class="ride-ordering-options-panel">
          <div
            v-loading="status.isLoading"
            class="ride-ordering-step-container"
            :element-loading-text="status.loadingMessage"
            element-loading-custom-class="custom-spinner-class"
          >
            <div class="timing-section">
              <el-radio-group
                v-model="rideFormType"
                class="timing-radio-group"
                :disabled="step !== RideOrderingStep.Location"
              >
                <el-radio label="now">
                  Now
                </el-radio>
                <el-radio
                  label="schedule"
                  :disabled="isNashDelivery"
                >
                  Later {{ isNashDelivery ? '(Only available for transportation)' : '' }}
                </el-radio>
              </el-radio-group>
            </div>

            <div
              v-if="isScheduleRide && isScheduledRideRestricted"
              class="scheduled-ride-notice"
            >
              <div class="alert alert-danger scheduled-ride-restriction-alert mb-0">
                This partner does not allow scheduling. Please read the Subsidy notes and contact your TL with questions.
                {{ customerInfo?.partner?.config ? 'Will Call rides are allowed.' : '' }}
              </div>
            </div>

            <BasicRideLimitValueUpgradeBanner class="ride-ordering-v2-upgrade-banner" />

            <RideLocation
              v-if="step === RideOrderingStep.Location"
              @get-estimate="getEstimate"
            />
            <RideEstimatePanel
              v-if="step === RideOrderingStep.Estimate"
              @edit="onEditEstimate"
              @order-ride="onOrderRide"
              @schedule-ride="onScheduleRide"
              @reset="resetToGetEstimate"
            />
            <RideStatusPanel
              v-if="step === RideOrderingStep.Order"
              @reset="resetToGetEstimate"
            />
          </div>

          <div
            v-if="!isScheduleRide"
            class="px-3 mt-10 next-ride-check-container"
          >
            <NextRideCheck
              :caller="customerInfo"
              :total-rides="totalOrderInfo?.totalRides"
              :is-one-way="orderRideRequest.serviceType === RideServiceType.Transportation"
              :matching="isMatchingDriver"
            />
          </div>
        </div>

        <div
          v-loading="status.isLoading"
          class="ride-ordering-map-panel"
          :element-loading-text="status.loadingMessage"
          element-loading-custom-class="custom-spinner-class"
        >
          <div class="location-map-section">
            <div class="location-map-label">
              Pickup
            </div>
            <RideMap
              type="pickup"
              :venues="pickupVenuesPlaces"
              :originalAddress="pickupLocation"
            />
          </div>
          <div
            v-for="(stop, index) in stops"
            :key="index"
            class="location-map-section"
          >
            <div class="location-map-label">
              Stop {{ index + 1 }}
            </div>
            <RideMap
              type="stop"
              :stopIndex="index"
              :venues="stopVenuesPlaces[index]"
              :originalAddress="stops[index]"
            />
          </div>
          <div class="location-map-section">
            <div class="location-map-label">
              Dropoff
            </div>
            <RideMap
              type="dropoff"
              :venues="dropoffVenuesPlaces"
              :originalAddress="dropOffLocation"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, nextTick, computed, defineAsyncComponent, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Moment from 'moment';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { scheduledRideService } from '@/services/scheduledRide';
import { sendPost } from '@/Utils/api';
import {
  step,
  rideFormType,
  orderRideRequest,
  status,
  customerInfo,
  initDefaults,
  rideOrderData,
  buildCallRideUrlFromRideOrderData,
  operatorConfirmedSurgePriceWithCustomer,
  rideDetails,
  initRideData,
  newPreferredContactNumber,
  estimationRide,
  estimationList,
  isInLyftAssistedTestGroup,
  isEstimationOptionsMode,
  setPartnerEstimatesFromResponse,
  applyPartnerEstimationData,
  resetPartnerEstimates,
  selectedPartnerId,
  notesForLP,
  isNeededToMonitorRide,
  scheduledRideId,
  rideTypeOptions,
  isExpandDriverSearch,
  isExpandVehicleSearch,
  isScheduleRide,
  isScheduledRideRestricted,
  checkAndShowAdaDialog,
  driverNotePickUp,
  riderNote,
  deliveryInfoFormRef,
  deliveryInfoResult,
  isDelivery,
  isNashDelivery,
  emergencyReason,
  approvedNameForEmergencyRide,
} from './RideOrderingV2/rideOrderingV2ViewModel';
import {
  pickupLocation,
  dropOffLocation,
  clearPickup,
  clearDropoff,
  clearMultiStops,
  selectLocation,
  pickupVenueNote,
  dropoffVenueNote,
  isRideOutsideUS,
  stops,
  pickupVenuesPlaces,
  dropoffVenuesPlaces,
  stopVenuesPlaces,
  pickupLocationContext,
  dropoffLocationContext,
} from './RideOrderingV2/rideLocationsViewModel';
import { RideServiceType, type OrderDataType, MedicalTripResult } from '@/models/ride';
import { useRide } from '@/use/ride';
import { useCustomer } from '@/use/customer';
import { formatDeliveryValidationErrorMessage } from '@/Utils/deliveryInfoForm';
import { ScheduledRideInput, type ExtendedScheduledRide } from '@/models/scheduledRide';
import { RideOrderingStep, OrderedRideDetail, type LocationItem } from '@/models/rideOrdering';
import rideUtility from '@/Utils/rideUtility';

import BasicRideLimitValueUpgradeBanner from './RideOrdering/BasicRideLimitValueUpgradeBanner.vue';
import { RideType, VendorName } from '@/models/rideType';
import type { CustomLocationType } from '@/models/address';

const RideLocation = defineAsyncComponent(() => import('./RideOrderingV2/RideLocation.vue'));
const RideEstimatePanel = defineAsyncComponent(() => import('./RideOrderingV2/RideEstimate.vue'))
const RideMap = defineAsyncComponent(() => import('./RideOrderingV2/RideMap.vue'))
const RideStatusPanel = defineAsyncComponent(() => import('./RideOrderingV2/RideStatus.vue'))
const NextRideCheck = defineAsyncComponent(() => import('./RideOrdering/NextRideCheck/NextRideCheck.vue'))
const { totalOrderInfo } = useCustomer();

const route = useRoute();
const router = useRouter();
const { showSuccessPopupDialog, showErrorPopupDialog, showWarningPopupDialog } = useSwalDialog();
const {
  checkPharmacyQuestions,
  checkSpecialRequestQuestions,
  duplicatedScheduledRide,
  rideOrderData: existingRideOrderData, // From RideHistory page to re-order or order return ride
  setDuplicatedScheduledRide,
  setRideOrderData,
  checkMedicalTripForDutchesPartnership,
  checkMedicalTripForDiamondBarPartnership,
  checkMedicalTripForRotcPartnership,
} = useRide();

const isSpecialRequestOrder = computed(() => orderRideRequest.value.serviceType === RideServiceType.SpecialRequest);
const panelSubtitle = computed(() => {
  if (step.value === RideOrderingStep.Location) {
    if (!pickupLocation.value) {
      return 'Select your pickup location';
    }
    if (!dropOffLocation.value) {
      return 'Select your drop-off location';
    }
    return 'Please get ride estimate';
  }

  if (step.value === RideOrderingStep.Estimate) {
    if (status.isUberSurge) {
      return 'Please confirm surge price';
    }
    return 'Please order a ride';
  }

  if (step.value === RideOrderingStep.Order) {
    if (status.errorMessage) {
      return 'Failed ordering a ride';
    }
    if (rideDetails.value?.rideStatus) {
      return 'Ordered a ride successfully';
    }
    return 'Ride ordered';
  }

  return '';
});

const getRideDateLocal = () => {
  if (orderRideRequest.value.scheduleKind === 'appointment') {
    const guessedRideDuration = 15;
    const { scheduledAppointmentBufferMinutes, scheduledAppointmentTime, scheduledAppointmentDate } = orderRideRequest.value;
    const diffMinutes = (scheduledAppointmentBufferMinutes || 0) + guessedRideDuration;

    const pickupTime = Moment(scheduledAppointmentTime).subtract(diffMinutes, 'minutes').format('hh:mm A');
    return `${util.formatDateTime(scheduledAppointmentDate, 'YYYY-MM-DD')} ${pickupTime}`;
  }

  const pickupTime = Moment(orderRideRequest.value.pickupTime).format('hh:mm A');
  if (scheduledRideId.value && orderRideRequest.value.scheduleKind === 'multi' && orderRideRequest.value.nextPickupDate) {
    return `${util.formatDateTime(orderRideRequest.value.nextPickupDate, 'YYYY-MM-DD')} ${pickupTime}`;
  }

  return `${util.formatDateTime(orderRideRequest.value.pickupDate, 'YYYY-MM-DD')} ${pickupTime}`;
};

const validateDeliveryInfo = () => {
  if (!isNashDelivery.value) {
    return true;
  }

  const validation = deliveryInfoFormRef.value?.validate() ?? {
    isValid: false,
    errors: [],
  };

  if (!validation.isValid) {
    status.errorMessage = formatDeliveryValidationErrorMessage(validation.errors);
  }

  return validation.isValid;
};

// Deliveries in the Nash test group collect this information through DeliveryInfoForm instead.
const checkServiceTypeQuestions = async () => {
  if (!isDelivery.value || isNashDelivery.value) {
    return true;
  }

  if (orderRideRequest.value.operatorNote) {
    return true;
  }

  const isOrderRideNow = !isScheduleRide.value;
  const response = isSpecialRequestOrder.value
    ? await checkSpecialRequestQuestions(isOrderRideNow)
    : await checkPharmacyQuestions(isOrderRideNow);

  if (!response) {
    return false;
  }

  orderRideRequest.value.operatorNote = response.notes;

  if (response.rideType) {
    orderRideRequest.value.rideType = response.rideType;
  }

  return true;
};

const checkMedicalTripForPartnerships = async () => {
  orderRideRequest.value.isMedicalTrip = false;
  status.loadingMessage = 'Checking medical trip for partnership';
  status.isLoading = true;

  const startAddress: CustomLocationType = {
    name: pickupLocation.value?.name ?? '',
    streetAddress: pickupLocation.value?.streetAddress ?? '',
    streetAddress2: pickupLocation.value?.streetAddress2 ?? '',
    city: pickupLocation.value?.city ?? '',
    state: pickupLocation.value?.state ?? '',
    zip: pickupLocation.value?.zip ?? '',
    country: pickupLocation.value?.country ?? '',
    latitude: pickupLocation.value?.latitude ?? null,
    longitude: pickupLocation.value?.longitude ?? null,
  };

  const endAddress: CustomLocationType = {
    name: dropOffLocation.value?.name ?? '',
    streetAddress: dropOffLocation.value?.streetAddress ?? '',
    streetAddress2: dropOffLocation.value?.streetAddress2 ?? '',
    city: dropOffLocation.value?.city ?? '',
    state: dropOffLocation.value?.state ?? '',
    zip: dropOffLocation.value?.zip ?? '',
    country: dropOffLocation.value?.country ?? '',
    latitude: dropOffLocation.value?.latitude ?? null,
    longitude: dropOffLocation.value?.longitude ?? null,
  };

  const resultDutchesPartnership = await checkMedicalTripForDutchesPartnership(startAddress, endAddress);
  const resultDiamondBarPartnership = await checkMedicalTripForDiamondBarPartnership(startAddress, endAddress);
  const resultRotcPartnership = await checkMedicalTripForRotcPartnership(startAddress, endAddress);

  status.isLoading = false;

  if (
    resultDutchesPartnership === MedicalTripResult.VALIDATION_FAILED ||
    resultDiamondBarPartnership === MedicalTripResult.VALIDATION_FAILED ||
    resultRotcPartnership === MedicalTripResult.VALIDATION_FAILED
  ) {
    return false;
  }

  if (
    resultDutchesPartnership === MedicalTripResult.USER_DECLINED ||
    resultDiamondBarPartnership === MedicalTripResult.USER_DECLINED ||
    resultRotcPartnership === MedicalTripResult.USER_DECLINED
  ) {
    orderRideRequest.value.isDoNotApplySubsidy = true;
  }

  if (
    resultDutchesPartnership === MedicalTripResult.NO_MEDICAL_PLACE ||
    resultDiamondBarPartnership === MedicalTripResult.NO_MEDICAL_PLACE
  ) {
    orderRideRequest.value.isDoNotApplySubsidy = true;
  }

  if (
    resultDutchesPartnership === MedicalTripResult.VALIDATION_PASSED ||
    resultDiamondBarPartnership === MedicalTripResult.VALIDATION_PASSED ||
    resultRotcPartnership === MedicalTripResult.VALIDATION_PASSED
  ) {
    orderRideRequest.value.isMedicalTrip = true;
  }

  return true;
};

const getEstimate = async () => {
  if (!pickupLocation.value || !dropOffLocation.value) return;

  const rideTypeItem = RideType.findByKey(orderRideRequest.value.rideType);
  if (isRideOutsideUS.value && rideTypeItem?.vendor === VendorName.LYFT) {
    showWarningPopupDialog({
      text: 'Lyft rides are not supported outside US.',
    });
    return;
  }

  if (!validateDeliveryInfo()) {
    return;
  }

  const resultMedicalTrip = await checkMedicalTripForPartnerships();

  if (!resultMedicalTrip) {
    return;
  }

  if (!(await checkServiceTypeQuestions())) {
    return;
  }

  const pickup = pickupLocation.value;
  const dropOff = dropOffLocation.value;

  status.isLoading = true;
  status.loadingMessage = 'Getting Estimation...';
  status.errorMessage = '';

  stops.value = stops.value.filter(Boolean);

  const payload: Record<string, any> = {
    callerId: customerInfo.value?.id,
    phoneNumber: orderRideRequest.value.phoneNumber || customerInfo.value?.phoneNumber,
    riderPhone: orderRideRequest.value.riderPhone || customerInfo.value?.phoneNumber,
    rideType: orderRideRequest.value.rideType,
    serviceType: orderRideRequest.value.serviceType,
    startAddress: pickup.streetAddress,
    startCity: pickup.city,
    startState: pickup.state,
    startZip: pickup.zip,
    startName: pickup.name,
    startAutoSaveLocation: pickupLocationContext.value?.autoSaveLocation ?? false,
    startSavedAddressIdentifier: pickupLocationContext.value?.savedAddressIdentifier,
    startCoors: {
      lat: pickup.latitude,
      lng: pickup.longitude,
    },
    endAddress: dropOff.streetAddress,
    endCity: dropOff.city,
    endState: dropOff.state,
    endZip: dropOff.zip,
    endName: dropOff.name,
    endAutoSaveLocation: dropoffLocationContext.value?.autoSaveLocation ?? false,
    endSavedAddressIdentifier: dropoffLocationContext.value?.savedAddressIdentifier,
    endCoors: {
      lat: dropOff.latitude,
      lng: dropOff.longitude,
    },
    destinations: stops.value.length > 0 ? {
      stops: stops.value.map((stop: LocationItem) => ({
        streetAddress: stop.streetAddress,
        city: stop.city?.trim() ?? '',
        state: stop.state?.trim() ?? '',
        zip: stop.zip?.trim() ?? '',
        lat: Number(stop.latitude),
        lng: Number(stop.longitude),
      })),
    } : undefined,
    isEmergency: orderRideRequest.value.isEmergency ? 1 : 0,
    emergencyReason: orderRideRequest.value.isEmergency
        ? `${emergencyReason.value}\n\nEmergency ride was approved by ${approvedNameForEmergencyRide.value}`
        : null,
    isMedicalTrip: orderRideRequest.value.isMedicalTrip,
    isDoNotApplySubsidy: orderRideRequest.value.isDoNotApplySubsidy ? 1 : 0,
  };

  // Only send Uber place ids when a Venue Entrance / Side is selected
  if (rideUtility.isVenueEntranceSelected(pickup)) {
    payload.startLocationId = pickup.locationId;
    payload.startZoneId = pickup.zoneId;
    payload.startVenueId = pickup.venueId;
    if (pickupVenueNote.value) {
      payload.venuesPlaceInstructions = pickupVenueNote.value;
    }
  }

  if (orderRideRequest.value.operatorNote) {
    payload.operatorNote = orderRideRequest.value.operatorNote;
  }

  if (orderRideRequest.value.reason) {
    payload.reason = orderRideRequest.value.reason;
  }

  if (isNashDelivery.value && deliveryInfoResult.value.payload) {
    Object.assign(payload, deliveryInfoResult.value.payload, {
      notesForLP: deliveryInfoResult.value.notesForLP,
      noteToDriver: '',
    });
  }

  if (isScheduleRide.value) {
    payload.rideDateLocal = getRideDateLocal();
    payload.overrideActiveRideCheck = true;
    payload.isChargeRideOperatingFee = false;

    if (orderRideRequest.value.scheduleKind === 'willcall') {
      payload.isWillCall = true;
    }
  }

  try {
    const isSingleEstimate = isScheduleRide.value;
    if (!isSingleEstimate) {
      payload.rideType = undefined;
      payload.isAllVendors = true;
    }

    const apiName = isSingleEstimate ? 'getWebEstimate' : 'getWebEstimateOptions'
    const response = await sendPost(apiName, payload);
    const data = response.data;

    if (data.message) {
      status.errorMessage = data.message;
      return;
    }

    if (!isSingleEstimate && !rideUtility.hasEstimateOptions(data)) {
      status.errorMessage = rideUtility.getNoEstimateOptionsMessage(data);
      return;
    }

    isEstimationOptionsMode.value = !isSingleEstimate;
    const estimationData = setPartnerEstimatesFromResponse(data);
    await applyPartnerEstimationData(estimationData);

    step.value = RideOrderingStep.Estimate;

    await checkAndShowAdaDialog();
  } catch (error: any) {
    status.errorMessage = util.getErrorResponse(error, false);
  } finally {
    status.isLoading = false;
  }
};

const onEditEstimate = () => {
  isMatchingDriver.value = false;
  step.value = RideOrderingStep.Location;
  estimationRide.value = null;
  estimationList.value = [];
  resetPartnerEstimates();
  pickupVenueNote.value = '';
  dropoffVenueNote.value = '';
  isInLyftAssistedTestGroup.value = false;
  status.errorMessage = '';
  status.isUberSurge = false;
  operatorConfirmedSurgePriceWithCustomer.value = false;
};

const getNoteToDriver = () => {
  let noteToDriver = util.makePickupDropoffDriverNotes(driverNotePickUp.value, dropoffVenueNote.value);

  if (riderNote.value) {
    noteToDriver += rideUtility.prefixNotes(riderNote.value);
  }

  return noteToDriver;
};

const isMatchingDriver = ref(false);

watch(
  () => customerInfo.value?.id,
  () => {
    isMatchingDriver.value = false;
  },
);

const onOrderRide = async () => {
  if (!rideOrderData.value) return;

  rideOrderData.value.noteToDriver = getNoteToDriver();
  isMatchingDriver.value = true;

  rideOrderData.value.partnerId = selectedPartnerId.value?.toString();

  let callRideFromEstimateLinkFinal = buildCallRideUrlFromRideOrderData(rideOrderData.value);

  if (status.isUberSurge) {
    callRideFromEstimateLinkFinal += '&operatorConfirmedSurgePriceWithCustomer=true';
  }

  try {
    status.loadingMessage = 'Ordering Ride...';
    status.isLoading = true;
    status.errorMessage = '';
    const response = await sendPost(callRideFromEstimateLinkFinal, {});
    fillAndDisplayRideDetails(response.data);
    step.value = RideOrderingStep.Order;
    status.isUberSurge = false;
    operatorConfirmedSurgePriceWithCustomer.value = false;
  } catch (error: any) {
    if (error.response && error.response.data) {
      const responseStatus = error.response.data.status || error.response.status;

      if (responseStatus === 409) {
        isMatchingDriver.value = false;
        if (error.response.data?.callRideFromEstimateLink) {
          estimationRide.value = error.response.data;
        }

        status.isUberSurge = true;
        status.errorMessage = util.getErrorResponse({
          message: 'Surge pricing detected. Please review the updated estimate and click "Accept Higher Price" to continue.',
        }, false);
      } else if (responseStatus === 504) {
        status.errorMessage = util.getErrorResponse({
          message: `We are still waiting for a driver to accept. Please hit Get Customer Info again and check the Latest Ride info on right panel.`,
        }, false);
      } else {
        const rideStatus = error.response.data.rideStatus;
        if (rideStatus) {
          fillAndDisplayRideDetails(error.response.data);
          step.value = RideOrderingStep.Order;
        } else {
          isMatchingDriver.value = false;
          status.errorMessage = util.getErrorResponse(error, false);
        }
      }
    } else {
      isMatchingDriver.value = false;
      status.errorMessage = util.getErrorResponse(error, false);
    }
  } finally {
    status.isLoading = false;
  }
};

const fillAndDisplayRideDetails = (response: OrderedRideDetail) => {
  const carColor = response?.carColor ?? '';
  const carBrand = response?.carBrand ?? '';
  const carModel = response?.carModel ?? '';

  rideDetails.value = {
    eta: response?.eta ?? '',
    driverName: response?.driverName ?? '',
    driverPhone: response?.driverPhone ?? '',
    driverPlate: response?.driverPlate ?? '',
    carBrand,
    carModel,
    carColor,
    carImage: response?.carImage ?? '',
    mapUrl: response?.mapUrl ?? '',
    createdAt: response?.createdAt ?? '',
    rideStatus: response?.rideStatus ?? '',
    startLocation: response?.startLocation ?? '',
    endLocation: response?.destination ?? response?.endLocation ?? '',
    car: `${carColor} ${carBrand} ${carModel}`,
    message: response?.message && util.setSuccessMessage(response.message),
  };
};

const prefillOrderRideData = (data: OrderDataType) => {
  isExpandDriverSearch.value = data.isExpandDriverSearch ?? false;
  isExpandVehicleSearch.value = data.isExpandVehicleSearch ?? false;

  orderRideRequest.value = {
    phoneNumber: data.phoneNumber || '',
    riderPhone: data.riderPhone || '',
    rideType: data.typeOfCar || rideTypeOptions.value[0].key,
    serviceType: data.serviceType || RideServiceType.Transportation,
  };

  const pickupAddress = {
    id: 'ordered-ride-pickup',
    name: data.startAddress?.name ?? '',
    streetAddress: data.startAddress?.streetAddress ?? '',
    city: data.startAddress?.city ?? '' ,
    state: data.startAddress?.state ?? '',
    zip: data.startAddress?.zip ?? '',
    latitude: data.startAddress?.latitude ?? null,
    longitude: data.startAddress?.longitude ?? null,
  };

  selectLocation(pickupAddress);

  const dropoffAddress = {
    id: 'ordered-ride-dropoff',
    name: data.endAddress?.name ?? '',
    streetAddress: data.endAddress?.streetAddress ?? '',
    city: data.endAddress?.city ?? '' ,
    state: data.endAddress?.state ?? '',
    zip: data.endAddress?.zip ?? '',
    latitude: data.endAddress?.latitude ?? null,
    longitude: data.endAddress?.longitude ?? null,
  };

  selectLocation(dropoffAddress);
};

const formatScheduledRideData = (data: ExtendedScheduledRide) => {
  const scheduleKind = data.rideMultiple
    ? 'multi'
    : data.scheduledAppointmentDate
      ? 'appointment'
      : 'single';

  orderRideRequest.value = {
    phoneNumber: data.callerPhoneNumber ?? '',
    riderPhone: data.riderPhone ?? '',
    callToConfirmSurge: data.callToConfirmSurge ? 'yes' : 'no',
    rideName: data.rideName ?? '',
    rideType: data.rideType ?? rideTypeOptions.value[0].key,
    serviceType: data.serviceType ?? RideServiceType.Transportation,
    minutesBeforeCustomReminder: data.minutesBeforeCustomReminder ?? undefined,
    minutesAfterCreationCustomReminder: data.minutesAfterCreationCustomReminder ?? undefined,
    scheduleKind,
    pickupDate: data.pickupDate ? new Date(`${data.pickupDate} 00:00:00`) : null,
    pickupTime: data.pickupTime ? new Date(util.convertTime(data.pickupTime || '')) : null,
    repeatEveryXWeek: data.repeatEveryXWeek ?? 1,
    day0: data.day0 ?? false,
    day1: data.day1 ?? false,
    day2: data.day2 ?? false,
    day3: data.day3 ?? false,
    day4: data.day4 ?? false,
    day5: data.day5 ?? false,
    day6: data.day6 ?? false,
    endOnType: 'never',
    endAfterXTimes: data.endAfterXTimes ?? 1,
    endOn: data.endOn ? new Date(data.endOn) : null,
    scheduledAppointmentDate: data.scheduledAppointmentDate ? new Date(`${data.pickupDate} 00:00:00`) : null,
    scheduledAppointmentTime: data.scheduledAppointmentTime ? new Date(util.convertTime(data.scheduledAppointmentTime || '')) : null,
    scheduledAppointmentBufferMinutes: data.scheduledAppointmentBufferMinutes ?? 0,
    overrideBadDriver: data.overrideBadDriver ?? false,
    overrideBigCar: data.overrideBigCar ?? false,
    overrideSmallCar: data.overrideSmallCar ?? false,
    overrideBannedCar: data.overrideBannedCar ?? false,
  }

  if (data.overrideBadDriver) {
    isExpandDriverSearch.value = true;
  }

  if (data.overrideBigCar && data.overrideSmallCar && data.overrideBannedCar) {
    isExpandVehicleSearch.value = true;
  }

  if (orderRideRequest.value.endOn && orderRideRequest.value.endAfterXTimes) {
    orderRideRequest.value.endOnType = 'never';
  } else if (!orderRideRequest.value.endOn && orderRideRequest.value.endAfterXTimes) {
    orderRideRequest.value.endOnType = 'after';
  } else if (orderRideRequest.value.endOn) {
    orderRideRequest.value.endOnType = 'on';
  }

  const nextPickupTime = data.nextPickupTimeString ? Moment(data.nextPickupTimeString) : null;

  if (scheduleKind === 'multi' && nextPickupTime?.isAfter(Moment())) {
    orderRideRequest.value.nextPickupDate = nextPickupTime.format('YYYY-MM-DD');
  }

  const pickupAddress = {
    id: 'scheduled-pickup',
    name: data.locationName1 || '',
    streetAddress: data.streetAddress1,
    city: data.city1,
    state: data.state1,
    zip: data.zip1,
    latitude: data.lat1 ?? null,
    longitude: data.long1 ?? null,
  };

  selectLocation(pickupAddress)

  const dropoffAddress = {
    id: 'scheduled-dropoff',
    name: data.locationName2 || '',
    streetAddress: data.streetAddress2,
    city: data.city2,
    state: data.state2,
    zip: data.zip2,
    latitude: data.lat2 ?? null,
    longitude: data.long2 ?? null,
  };

  selectLocation(dropoffAddress)
}

const getScheduledRideData = async () => {
  status.loadingMessage = 'Loading Scheduled Ride Data...';
  status.isLoading = true;

  try {
    const response = await scheduledRideService.fetchScheduledRideRecords({
      scheduledRideId: scheduledRideId.value,
    });

    if (response.count === 0) {
      return;
    }

    const data = response.rows[0];
    formatScheduledRideData(data);
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
  }
};

const onScheduleRide = async () => {
  if (!rideOrderData.value) {
    return;
  }

  const isWillCall = orderRideRequest.value.scheduleKind === 'willcall';

  if (orderRideRequest.value.scheduleKind === 'appointment') {
    const { scheduledAppointmentBufferMinutes, scheduledAppointmentTime, scheduledAppointmentDate } = orderRideRequest.value;
    const diffMinutes = (scheduledAppointmentBufferMinutes || 0) + estimationRide.value.duration;
    orderRideRequest.value.pickupTime = Moment(scheduledAppointmentTime).subtract(diffMinutes, 'minutes').toDate();
    orderRideRequest.value.pickupDate = scheduledAppointmentDate;
  }

  let payload: ScheduledRideInput = {
    phoneNumber: util.getValidPhoneNumber(orderRideRequest.value.phoneNumber || customerInfo.value?.phoneNumber),
    riderPhone: util.getValidPhoneNumber(orderRideRequest.value.riderPhone || newPreferredContactNumber.value),
    rideName: orderRideRequest.value.rideName,
    rideType: orderRideRequest.value.rideType,
    serviceType: orderRideRequest.value.serviceType,
    timeZone: customerInfo.value.timeZone,
    noteToDriver: getNoteToDriver(),
    notesForLP: isNeededToMonitorRide.value ? notesForLP.value || '' : '',
    partnerId: selectedPartnerId.value,
    isWillCall,
    scheduledAppointmentTime: orderRideRequest.value.scheduleKind === 'appointment' ? util.formatDateTime(orderRideRequest.value.scheduledAppointmentTime, 'hh:mm A') : null,
    scheduledAppointmentDate: orderRideRequest.value.scheduleKind === 'appointment' ? util.formatDateTime(orderRideRequest.value.scheduledAppointmentDate) : null,
    scheduledAppointmentBufferMinutes: orderRideRequest.value.scheduleKind === 'appointment' ? orderRideRequest.value.scheduledAppointmentBufferMinutes : null,
    pickupTime: util.formatDateTime(orderRideRequest.value.pickupTime, 'hh:mm A'),
    pickupDate: util.formatDateTime(orderRideRequest.value.pickupDate),
    streetAddress1: pickupLocation.value?.streetAddress ?? '',
    city1: pickupLocation.value?.city ?? '',
    state1: pickupLocation.value?.state ?? '',
    zip1: pickupLocation.value?.zip ?? '',
    lat1: Number(rideOrderData.value.startLat),
    long1: Number(rideOrderData.value.startLong),
    streetAddress2: dropOffLocation.value?.streetAddress,
    city2: dropOffLocation.value?.city,
    state2: dropOffLocation.value?.state,
    zip2: dropOffLocation.value?.zip,
    lat2: Number(rideOrderData.value.destinationLat),
    long2: Number(rideOrderData.value.destinationLng),
    locationName1: rideOrderData.value.startName,
    locationName2: rideOrderData.value.destinationName,
    rideMultiple: false,
    isMedicalTrip: orderRideRequest.value.isMedicalTrip,
  };

  if (orderRideRequest.value.scheduleKind === 'multi') {
    payload = {
      ...payload,
      rideMultiple: true,
      repeatEveryXWeek: orderRideRequest.value.repeatEveryXWeek,
      day0: orderRideRequest.value.day0,
      day1: orderRideRequest.value.day1,
      day2: orderRideRequest.value.day2,
      day3: orderRideRequest.value.day3,
      day4: orderRideRequest.value.day4,
      day5: orderRideRequest.value.day5,
      day6: orderRideRequest.value.day6,
      endOn: orderRideRequest.value.endOnType === 'on' ? util.formatDateTime(orderRideRequest.value.endOn, 'YYYY-MM-DD') : null,
      endAfterXTimes: orderRideRequest.value.endOnType === 'after' ? orderRideRequest.value.endAfterXTimes : null,
    };
  }

  const isTransportation = orderRideRequest.value.serviceType === RideServiceType.Transportation;
  if (isTransportation) {
    payload = {
      ...payload,
      callToConfirmSurge: orderRideRequest.value.callToConfirmSurge === 'yes' ? 1 : 0,
      minutesBeforeCustomReminder: orderRideRequest.value.minutesBeforeCustomReminder ? Number(orderRideRequest.value.minutesBeforeCustomReminder) : null,
      minutesAfterCreationCustomReminder: orderRideRequest.value.minutesAfterCreationCustomReminder ? Number(orderRideRequest.value.minutesAfterCreationCustomReminder) : null,
      isDoNotApplySubsidy: orderRideRequest.value.isDoNotApplySubsidy ? Number(orderRideRequest.value.isDoNotApplySubsidy) : 0,
      isDoNotApplyExpiringCredits: orderRideRequest.value.isDoNotApplyExpiringCredits
        ? Number(orderRideRequest.value.isDoNotApplyExpiringCredits)
        : 0,
      overrideBigCar: orderRideRequest.value.overrideBigCar ? Number(orderRideRequest.value.overrideBigCar) : 0,
      overrideSmallCar: orderRideRequest.value.overrideSmallCar ? Number(orderRideRequest.value.overrideSmallCar) : 0,
      overrideBannedCar: orderRideRequest.value.overrideBannedCar ? Number(orderRideRequest.value.overrideBannedCar) : 0,
      overrideBadDriver: orderRideRequest.value.overrideBadDriver ? Number(orderRideRequest.value.overrideBadDriver) : 0,
    };
  }

  if (estimationRide.value) {
    payload = {
      ...payload,
      rideCostId: estimationRide.value.rideCostId ? Number(estimationRide.value.rideCostId) : null,
      eta: estimationRide.value.eta,
      perMile: estimationRide.value.perMile != null ? estimationRide.value.perMile * 100 : undefined, // multiply with 100 to match RideEstimate records
      perMinute: estimationRide.value.perMinute != null ? estimationRide.value.perMinute * 100 : undefined, // multiply with 100 to match RideEstimate records
      baseFare: estimationRide.value.baseFare != null ? estimationRide.value.baseFare * 100 : undefined, // multiply with 100 to match RideEstimate records
      minimumFare: estimationRide.value.minimumFare != null ? estimationRide.value.minimumFare * 100 : undefined, // multiply with 100 to match RideEstimate records
      operatingFee: estimationRide.value.operatingFee != null ? estimationRide.value.operatingFee * 100 : undefined, // multiply with 100 to match RideEstimate records
      customerCostOverride: estimationRide.value.customerCostOverride?.toString(),
      currency: estimationRide.value.currency,
      surgeFactor: estimationRide.value.surgeFactor,
      surgeKey: estimationRide.value.surgeKey,
      surgeUrl: estimationRide.value.surgeUrl,
      duration: estimationRide.value.duration,
      distance: estimationRide.value.distance,
      lowEstimate: estimationRide.value.lowEstimate,
      highEstimate: estimationRide.value.highEstimate,
      fareId: estimationRide.value.fareId,
      pricingExplanation: estimationRide.value.pricingExplanation,
      storedLongRideOrderDataKey: estimationRide.value.storedLongRideOrderDataKey,
    };
  }

  try {
    status.loadingMessage = !scheduledRideId.value ? 'Creating Ride Scheduler...' : 'Updating Ride Scheduler...';
    status.isLoading = true;
    status.errorMessage = '';

    if (scheduledRideId.value) {
      await scheduledRideService.updateScheduledRide({
        scheduledRideId: scheduledRideId.value,
        data: payload,
      });

      await showSuccessPopupDialog({
        text: `Your scheduled ride has been updated. Confirmation number: ${scheduledRideId.value}`,
      });

      // It is needed to remove scheduledRideId from the url after scheduledRide is updated
      scheduledRideId.value = 0;
      router.replace({ query: {
        rideFormType: rideFormType.value,
        phoneNumber: customerInfo.value?.phoneNumber,
      } });
    } else {
      const disallowVendorScheduledRide = orderRideRequest.value.scheduleKind === 'appointment' || orderRideRequest.value.serviceType !== RideServiceType.Transportation;
      const response = isWillCall
        ? await scheduledRideService.createWillCallRide(payload)
        : await scheduledRideService.createScheduledRide(payload, disallowVendorScheduledRide);

      await showSuccessPopupDialog({
        text: `${isWillCall ? '"Will Call"' : 'Scheduled'} ride has been created. Confirmation number: ${response.id}`,
      });
    }

    resetToGetEstimate();
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
  }
};

const resetToGetEstimate = (isNeededToReorder: boolean = false) => {
  isMatchingDriver.value = false;

  if (isNeededToReorder) {
    step.value = RideOrderingStep.Estimate;
    rideDetails.value = null;
    return;
  }

  step.value = RideOrderingStep.Location;
  rideOrderData.value = null;
  status.errorMessage = '';
  status.isUberSurge = false;
  operatorConfirmedSurgePriceWithCustomer.value = false;
  estimationList.value = [];
  estimationRide.value = null;
  resetPartnerEstimates();
  clearPickup();
  clearDropoff();
  clearMultiStops();
  initRideData();

  if (scheduledRideId.value) {
    getScheduledRideData();
  }
};

onMounted(async () => {
  const hash = route.hash;
  await nextTick();
  util.moveScrollComponent(hash);

  rideFormType.value = (route.query.rideFormType as string) || 'now';
  scheduledRideId.value = route.query.scheduledRideId ? Number(route.query.scheduledRideId as string) : 0;

  if (scheduledRideId.value) {
    resetToGetEstimate();
  } else if (duplicatedScheduledRide.value) {
    resetToGetEstimate();
    formatScheduledRideData(duplicatedScheduledRide.value);
    setDuplicatedScheduledRide(null);
  } else if (existingRideOrderData.value) {
    resetToGetEstimate();
    prefillOrderRideData(existingRideOrderData.value);
    setRideOrderData(null);
  }
});
</script>

<style lang="scss">
.ride-ordering-v2 {
  background: #fff;
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  overflow: hidden;

  .ordering-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .panel-content {
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow: hidden;
  }

  .ride-ordering-options-panel {
    width: 420px;
    min-width: 420px;
    background: #fff;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #e5e5e5;
    overflow: hidden;
  }

  .ride-ordering-step-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }

  .next-ride-check-container {
    flex-shrink: 0;
  }

  .panel-header {
    color: #fff;
    padding: 20px 24px;
    background: white;
    height: auto;
  }

  .panel-title {
    font-size: 24px;
    font-weight: 300;
    margin: 0 0 4px 0;
    color: #252422;
  }

  .panel-subtitle {
    font-size: 14px;
    color: #a0a0a0;
    margin: 0;
  }

  .timing-section {
    padding: 16px 24px;
    border-top: 1px solid #e5e5e5;
    border-bottom: 1px solid #e5e5e5;
  }

  .scheduled-ride-notice {
    padding: 12px 24px 0;
    flex-shrink: 0;
  }

  .scheduled-ride-restriction-alert.alert-danger {
    background-color: #dc3545;
  }

  .ride-ordering-v2-upgrade-banner {
    margin: 12px 24px 0;
    flex-shrink: 0;
  }

  .timing-radio-group {
    display: flex;
    gap: 16px;

    .el-radio {
      margin-right: 0;
      height: auto;

      .el-radio__input {
        .el-radio__inner {
          width: 16px;
          height: 16px;
          border: 1px solid #d1d5db;
          background: #fff;

          &::after {
            width: 6px;
            height: 6px;
            background-color: #fff;
          }
        }
      }

      .el-radio__label {
        font-size: 14px;
        color: #111827;
        padding-left: 8px;
        font-weight: 400;
      }

      &.is-checked {
        .el-radio__input {
          .el-radio__inner {
            border-color: #111827;
            background: #fff;

            &::after {
              background-color: #111827;
            }
          }
        }

        .el-radio__label {
          color: #111827;
          font-weight: 500;
        }
      }

      &:hover {
        .el-radio__inner {
          border-color: #9ca3af;
        }
      }
    }
  }

  .ride-ordering-map-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    border: 1px solid #e5e5e5;
    border-left-width: 0;
  }

  .location-map-section {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    border-bottom: 1px solid #e5e5e5;

    &:last-child {
      border-bottom: none;
    }
  }

  .location-map-label {
    flex-shrink: 0;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #252422;
    background: #fafafa;
    border-bottom: 1px solid #e5e5e5;
  }
}

@media (max-width: 768px) {
  .ride-ordering-v2 {
    .ordering-container {
      flex-direction: column;
    }

    .ride-ordering-options-panel {
      width: 100%;
      min-width: 100%;
      max-height: 50vh;
      overflow-y: auto;
    }

    .ride-ordering-map-panel {
      min-height: 50vh;
    }
  }
}
</style>
